import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { Menu, X } from "lucide-react";
import { sections } from "../content";
import { useWhoami } from "../admin/api/whoami";
import { ThemeToggle } from "../components/ThemeToggle";

/** Shared nav content — rendered in both desktop sidebar and mobile drawer. */
function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const { data: whoami } = useWhoami();
  const isAdmin = whoami?.isAdmin === true;
  const { pathname } = useLocation();

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-5 pb-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-sidebar-primary">
          Taiga
        </div>
        <div className="text-lg font-semibold text-foreground">Data Room</div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-5 text-sm">
        {sections.map((section, i) => (
          <div key={i}>
            {section.label && (
              <div className="px-2 text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                {section.label}
              </div>
            )}
            <ul className="space-y-0.5">
              {section.pages.map((page) => {
                const to = page.slug ? `/${page.slug}` : "/";
                const active =
                  page.slug === ""
                    ? pathname === "/"
                    : pathname === `/${page.slug}`;
                return (
                  <li key={page.slug}>
                    <NavLink
                      to={to}
                      onClick={onNavigate}
                      className={[
                        "block px-2 py-1.5 rounded-md transition-colors",
                        active
                          ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                          : "text-sidebar-foreground hover:bg-sidebar-accent/60",
                      ].join(" ")}
                    >
                      {page.title}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-sidebar-border px-5 py-4 text-xs text-muted-foreground flex flex-col gap-2">
        {isAdmin && (
          <NavLink
            to="/admin"
            onClick={onNavigate}
            className="hover:text-foreground transition-colors"
          >
            Admin →
          </NavLink>
        )}
        <a
          href="/auth/logout"
          className="hover:text-foreground transition-colors"
        >
          Sign out
        </a>
        <div className="flex items-center pt-1 -ml-1.5">
          <ThemeToggle />
        </div>
      </div>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export function RoomLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close on escape key
  useEffect(() => {
    if (!mobileOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [mobileOpen]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 shrink-0 border-r border-sidebar-border bg-sidebar sticky top-0 h-screen overflow-y-auto">
        <SidebarNav />
      </aside>

      {/* Mobile drawer — CSS transform, no library dependency */}
      <div
        className={`fixed inset-0 z-50 md:hidden transition-visibility ${mobileOpen ? "visible" : "invisible"}`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${mobileOpen ? "opacity-100" : "opacity-0"}`}
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
        {/* Drawer panel */}
        <aside
          className={`absolute inset-y-0 left-0 w-72 bg-sidebar shadow-xl transition-transform duration-300 ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
        >
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="absolute top-4 right-4 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            aria-label="Close navigation"
          >
            <X className="size-4" />
          </button>
          <SidebarNav onNavigate={() => setMobileOpen(false)} />
        </aside>
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="flex items-center gap-3 border-b border-border px-4 py-3 md:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            aria-label="Open navigation"
          >
            <Menu className="size-5" />
          </button>
          <span className="text-sm font-semibold text-foreground">
            Taiga Data Room
          </span>
        </header>

        <ScrollToTop />
        <div className="flex-1 min-w-0">
          <div className="max-w-4xl mx-auto px-6 py-8 md:px-8 md:py-12">
            <article className="prose">
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </article>
          </div>
        </div>
      </div>
    </div>
  );
}
